import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Truck, Clock, Shield, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_print-hub-105/artifacts/zxnakunl_AdobeStock_1851367824.mov';

const categories = [
  {
    name: 'Advertising',
    image: 'https://static.prod-images.emergentagent.com/jobs/39cba615-f91e-4b44-abdf-1f947796cceb/images/7b0ecafba9ce761047b5531c5424564f94ed64ab87a80b220ac5b7db48b451e0.jpeg',
    link: '/products?category=Advertising',
  },
  {
    name: 'Stationery & Cards',
    image: 'https://static.prod-images.emergentagent.com/jobs/39cba615-f91e-4b44-abdf-1f947796cceb/images/73be3a2753ee8e2ef76c8ea5ca3d23d8883ffef2cc5f7ac187c16cbde0879b9e.jpeg',
    link: '/products?category=Stationery & Cards',
  },
  {
    name: 'Packaging',
    image: 'https://static.prod-images.emergentagent.com/jobs/39cba615-f91e-4b44-abdf-1f947796cceb/images/daf12cd60028dc2ec7a5f033dce9f4bf5b2c1341b0e432127ff01ae73b33458f.jpeg',
    link: '/products?category=Packaging',
  },
  {
    name: 'Outdoor Displays',
    image: 'https://static.prod-images.emergentagent.com/jobs/39cba615-f91e-4b44-abdf-1f947796cceb/images/81859af7f60416097dfd3eb012109c312a60cc1cd1761fd43c0e8aa5a43fb11e.jpeg',
    link: '/products?category=Outdoor Displays',
  },
];

const features = [
  { icon: Clock, title: 'Fast Turnaround', description: 'Quick production and delivery times' },
  { icon: Shield, title: 'Quality Guaranteed', description: 'Premium materials and printing' },
  { icon: Truck, title: 'Free Delivery', description: 'On orders over $50' },
  { icon: Star, title: 'Custom Design', description: 'Professional design services' },
];

export function HomePage() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/reviews`).then(res => setReviews(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Video Background */}
      <section className="relative overflow-hidden min-h-[600px] lg:min-h-[700px] flex items-center">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/quicktime" />
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Overlay */}
        <div className="absolute inset-0 bg-navy/65" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-sm text-mint uppercase tracking-[0.3em] font-semibold">
                Design • Print • Deliver
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-black text-white tracking-tighter leading-[1.05]">
                Bring Your Ideas<br />
                <span className="text-mint">To Life</span>
              </h1>
              <p className="mt-6 text-lg text-gray-200 leading-relaxed max-w-xl">
                Professional printing services for businesses and individuals. From business cards to banners, we deliver quality prints right to your doorstep.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/products">
                  <Button className="bg-mint hover:bg-mint-dark text-black px-8 py-6 text-base rounded-full btn-hover" data-testid="shop-now-btn">
                    Shop Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/custom-design">
                  <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base rounded-full btn-hover backdrop-blur-sm" data-testid="custom-design-btn">
                    Custom Design
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 inline-flex bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-mint/20 rounded-full flex items-center justify-center">
                  <Truck className="h-6 w-6 text-mint" />
                </div>
                <div>
                  <p className="font-semibold text-white">Fast Delivery</p>
                  <p className="text-sm text-gray-300">2-5 Business Days</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto bg-mint/10 rounded-2xl flex items-center justify-center mb-4">
                  <feature.icon className="h-7 w-7 text-mint" />
                </div>
                <h3 className="font-heading font-semibold text-navy">{feature.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm text-coral uppercase tracking-[0.2em] font-semibold">
              Our Products
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-navy">
              Shop by Category
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={category.link} className="block group" data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="relative rounded-2xl overflow-hidden card-hover bg-white border border-gray-200">
                    <div className="product-image-container aspect-[4/3]">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover product-image"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-heading font-semibold text-white text-xl">{category.name}</h3>
                      <span className="text-mint text-sm flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">
                        Shop Now <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-20 bg-navy">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-mint uppercase tracking-[0.2em] font-semibold">
                For Print Houses
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-white">
                Join Our Production Network
              </h2>
              <p className="mt-4 text-gray-300 text-lg leading-relaxed">
                Are you a print house looking for new business opportunities? Partner with Printed LB and receive steady orders from our growing customer base across Lebanon.
              </p>
              <Link to="/partners">
                <Button className="mt-8 bg-mint hover:bg-mint-dark text-black px-8 py-6 text-base rounded-full btn-hover" data-testid="partner-cta-btn">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwcHJpbnRpbmclMjBwcmVzcyUyMG1hY2hpbmV8ZW58MHx8fHwxNzg3ODE2NDM2fDA&ixlib=rb-4.1.0&q=85"
                alt="Printing press"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm text-mint uppercase tracking-[0.2em] font-semibold">Testimonials</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-navy">What Our Customers Say</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((review, i) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-100 relative"
                >
                  <Quote className="absolute top-4 right-4 h-8 w-8 text-mint/15" />
                  <div className="flex gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <h4 className="font-heading font-semibold text-navy text-sm">{review.title}</h4>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed line-clamp-3">{review.message}</p>
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-navy">{review.user_name}</p>
                    <p className="text-[10px] text-gray-400">{new Date(review.created_at).toLocaleDateString()}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Custom Design CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy">
                Need a Custom Design?
              </h2>
              <p className="mt-4 text-gray-600 text-lg leading-relaxed">
                Our team of professional designers can bring your vision to life. Upload your ideas and let us create something amazing for you.
              </p>
              <Link to="/custom-design">
                <Button className="mt-8 bg-coral hover:bg-coral-dark text-white px-8 py-6 text-base rounded-full btn-hover" data-testid="start-project-btn">
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/5706020/pexels-photo-5706020.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Custom design service"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy">
              Have a Large Order?
            </h2>
            <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
              Get a personalized quote for bulk orders. We offer competitive pricing for businesses and organizations.
            </p>
            <Link to="/quote">
              <Button className="mt-8 bg-mint hover:bg-mint-dark text-black px-8 py-6 text-base rounded-full btn-hover" data-testid="get-quote-btn">
                Get a Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
